/** Версия формата в localStorage. */
export const STORED_COLUMNS_VERSION = 1;
/** Сколько записей хранить; сверх — выбрасываются самые старые неизвестные. */
export const MAX_STORED_COLUMNS = 200;
const groupOf = (pin) => pin ?? 'none';
function indexDefs(defs) {
    return new Map(defs.map((def, index) => [def.id, { def, index }]));
}
function defaultEntry(def) {
    return {
        id: def.id,
        visible: def.hideable === false ? true : (def.defaultVisible ?? true),
        pin: def.pinnable === false ? null : (def.defaultPin ?? null),
    };
}
/** Ограничения описания сильнее сохранённого: обязательную не скрыть, незакрепляемую не закрепить. */
function constrain(entry, def) {
    const visible = def.hideable === false ? true : entry.visible;
    const pin = def.pinnable === false ? null : entry.pin;
    return visible === entry.visible && pin === entry.pin ? entry : { id: entry.id, visible, pin };
}
/**
 * Переставляет известные колонки по слотам, где они уже стоят: неизвестные
 * записи не двигаются, известные занимают свои слоты в новом порядке.
 */
function withKnownOrder(layout, known, order) {
    let k = 0;
    return layout.map((entry) => (known.has(entry.id) ? (order[k++] ?? entry) : entry));
}
function knownOf(layout, known) {
    return layout.filter((c) => known.has(c.id));
}
/** Порядок внутри группы: колонки без перестановки — к краю (первая по описанию — в начало). */
function arrangeGroup(items, known) {
    const head = [];
    const body = [];
    const tail = [];
    for (const c of items) {
        const meta = known.get(c.id);
        if (meta?.def.reorderable === false)
            (meta.index === 0 ? head : tail).push(c);
        else
            body.push(c);
    }
    const byDef = (a, b) => (known.get(a.id)?.index ?? 0) - (known.get(b.id)?.index ?? 0);
    return [...head.sort(byDef), ...body, ...tail.sort(byDef)];
}
function normalize(layout, known) {
    const constrained = layout.map((c) => {
        const meta = known.get(c.id);
        return meta ? constrain(c, meta.def) : c;
    });
    const groups = { left: [], none: [], right: [] };
    for (const c of knownOf(constrained, known))
        groups[groupOf(c.pin)].push(c);
    const order = [
        ...arrangeGroup(groups.left, known),
        ...arrangeGroup(groups.none, known),
        ...arrangeGroup(groups.right, known),
    ];
    return withKnownOrder(constrained, known, order);
}
/** Состояние по умолчанию — порядок описания с учётом закреплений по умолчанию. */
export function defaultColumnsState(defs) {
    return normalize(defs.map(defaultEntry), indexDefs(defs));
}
function isPin(v) {
    return v === 'left' || v === 'right' || v === null;
}
/**
 * Разбор сохранённого значения. Битое, чужой версии или не той формы — `null`
 * (значит, умолчание); ошибки не бросаются. Отдельные испорченные записи
 * пропускаются, повтор id — берётся первый. Неизвестные поля игнорируются
 * (зарезервировано, например, `width`).
 */
export function parseStoredColumns(raw) {
    if (!raw)
        return null;
    let data;
    try {
        data = JSON.parse(raw);
    }
    catch {
        return null;
    }
    if (typeof data !== 'object' || data === null)
        return null;
    const { v, columns } = data;
    if (v !== STORED_COLUMNS_VERSION || !Array.isArray(columns))
        return null;
    const seen = new Set();
    const out = [];
    for (const item of columns) {
        if (typeof item !== 'object' || item === null)
            continue;
        const { id, visible, pin } = item;
        if (typeof id !== 'string' || id === '' || seen.has(id))
            continue;
        if (typeof visible !== 'boolean')
            continue;
        const p = pin === undefined ? null : pin;
        if (!isPin(p))
            continue;
        seen.add(id);
        out.push({ id, visible, pin: p });
    }
    return { v: STORED_COLUMNS_VERSION, columns: out };
}
/** Значение для localStorage. */
export function serializeColumns(layout) {
    const value = {
        v: STORED_COLUMNS_VERSION,
        columns: layout.map(({ id, visible, pin }) => ({ id, visible, pin })),
    };
    return JSON.stringify(value);
}
/**
 * Сохранённое + описание → нормализованная раскладка.
 *
 * - Колонка из описания, которой нет в сохранённом, встаёт сразу после
 *   ближайшей предшествующей по описанию колонки (или в начало), с видимостью
 *   и закреплением по умолчанию.
 * - Неизвестные записи остаются на месте, но не больше `MAX_STORED_COLUMNS`
 *   всего — лишние неизвестные выбрасываются с начала.
 * - Если ни одна известная колонка не видима — умолчание.
 */
export function mergeColumnsState(defs, stored) {
    const known = indexDefs(defs);
    const layout = stored ? stored.columns.map((c) => ({ ...c })) : [];
    const position = new Map(layout.map((c, i) => [c.id, i]));
    defs.forEach((def, i) => {
        if (position.has(def.id))
            return;
        let at = 0;
        for (let j = i - 1; j >= 0; j--) {
            const prev = defs[j];
            const p = prev ? layout.findIndex((c) => c.id === prev.id) : -1;
            if (p >= 0) {
                at = p + 1;
                break;
            }
        }
        layout.splice(at, 0, defaultEntry(def));
        position.set(def.id, at);
    });
    let excess = layout.length - MAX_STORED_COLUMNS;
    const capped = excess > 0
        ? layout.filter((c) => {
            if (excess > 0 && !known.has(c.id)) {
                excess--;
                return false;
            }
            return true;
        })
        : layout;
    const merged = normalize(capped, known);
    // Ни одной видимой известной колонки (колонку убрали из описания или вид
    // пришёл из legacy) — пустая таблица хуже умолчания.
    if (defs.length > 0 && !merged.some((c) => known.has(c.id) && c.visible)) {
        return defaultColumnsState(defs);
    }
    return merged;
}
/**
 * Известные колонки в порядке отображения, с данными для отрисовки.
 * Смещения считаются только по видимым закреплённым.
 */
export function resolveColumns(layout, defs) {
    const known = indexDefs(defs);
    const items = layout.filter((c) => known.has(c.id));
    const left = items.filter((c) => c.visible && c.pin === 'left');
    const right = items.filter((c) => c.visible && c.pin === 'right');
    const width = (id) => known.get(id)?.def.minWidth ?? 0;
    return items.map((c) => {
        const def = known.get(c.id).def;
        let pinIndex = -1;
        let pinEdge = false;
        let pinOffsetFallback = 0;
        if (c.visible && c.pin === 'left') {
            pinIndex = left.indexOf(c);
            pinEdge = pinIndex === left.length - 1;
            for (let i = 0; i < pinIndex; i++)
                pinOffsetFallback += width(left[i].id);
        }
        else if (c.visible && c.pin === 'right') {
            const fromLeft = right.indexOf(c);
            pinIndex = right.length - 1 - fromLeft;
            pinEdge = fromLeft === 0;
            for (let i = fromLeft + 1; i < right.length; i++)
                pinOffsetFallback += width(right[i].id);
        }
        return { id: c.id, visible: c.visible, pin: c.pin, def, pinIndex, pinEdge, pinOffsetFallback };
    });
}
/** Видимые колонки в порядке отрисовки. */
export function renderOrder(layout, defs) {
    return resolveColumns(layout, defs).filter((c) => c.visible);
}
/** Совпадает ли раскладка (по известным колонкам) с умолчанием. */
export function isDefaultLayout(layout, defs) {
    const known = indexDefs(defs);
    const current = layout.filter((c) => known.has(c.id));
    const defaults = defaultColumnsState(defs);
    return (current.length === defaults.length &&
        current.every((c, i) => {
            const d = defaults[i];
            return d !== undefined && d.id === c.id && d.visible === c.visible && d.pin === c.pin;
        }));
}
/** Колонка — единственная видимая из известных (её скрывать нельзя). */
export function isLastVisibleColumn(layout, defs, id) {
    const visible = knownOf(layout, indexDefs(defs)).filter((c) => c.visible);
    return visible.length === 1 && visible[0].id === id;
}
export function setColumnVisible(layout, defs, id, visible) {
    const known = indexDefs(defs);
    if (!known.has(id))
        return layout;
    // Последнюю видимую не скрыть: таблица без колонок — пустая коробка.
    if (!visible && isLastVisibleColumn(layout, defs, id))
        return layout;
    return normalize(layout.map((c) => (c.id === id ? { ...c, visible } : c)), known);
}
/**
 * Закрепить или открепить. Закреплённая слева встаёт последней в левой группе
 * (ближе к данным), справа — первой в правой; открепленная возвращается на
 * ближний к своей бывшей группе край обычных колонок.
 */
export function setColumnPin(layout, defs, id, pin) {
    const known = indexDefs(defs);
    const meta = known.get(id);
    if (!meta || meta.def.pinnable === false)
        return layout;
    const order = knownOf(layout, known);
    const from = order.findIndex((c) => c.id === id);
    const current = order[from];
    if (!current || current.pin === pin)
        return layout;
    const prevGroup = groupOf(current.pin);
    order.splice(from, 1);
    const moved = { ...current, pin };
    const target = groupOf(pin);
    let at;
    if (target === 'left')
        at = order.filter((c) => c.pin === 'left').length;
    else if (target === 'right')
        at = order.findIndex((c) => c.pin === 'right');
    else if (prevGroup === 'left')
        at = order.filter((c) => c.pin === 'left').length;
    else
        at = order.findIndex((c) => c.pin === 'right');
    if (at < 0)
        at = order.length;
    order.splice(at, 0, moved);
    return normalize(withKnownOrder(layout, known, order), known);
}
/**
 * Где колонка может стоять: её позиция в известных и диапазон позиций, куда
 * её можно переставить (внутри своей группы, мимо колонок без перестановки не
 * пройти). `null` — колонку переставить нельзя.
 */
export function moveBounds(layout, defs, id) {
    const known = indexDefs(defs);
    const order = layout.filter((c) => known.has(c.id));
    const index = order.findIndex((c) => c.id === id);
    const self = order[index];
    if (!self || known.get(id)?.def.reorderable === false)
        return null;
    const group = groupOf(self.pin);
    let min = index;
    let max = index;
    const movable = (c) => c !== undefined && groupOf(c.pin) === group && known.get(c.id)?.def.reorderable !== false;
    while (movable(order[min - 1]))
        min--;
    while (movable(order[max + 1]))
        max++;
    return { index, min, max };
}
/** Поставить колонку на позицию `toIndex` среди известных (обрезается до допустимого). */
export function moveColumn(layout, defs, id, toIndex) {
    const bounds = moveBounds(layout, defs, id);
    if (!bounds)
        return layout;
    const to = Math.min(bounds.max, Math.max(bounds.min, Math.trunc(toIndex)));
    if (to === bounds.index)
        return layout;
    const known = indexDefs(defs);
    const order = knownOf(layout, known);
    const [item] = order.splice(bounds.index, 1);
    if (!item)
        return layout;
    order.splice(to, 0, item);
    return normalize(withKnownOrder(layout, known, order), known);
}
export function canMoveColumnBy(layout, defs, id, delta) {
    const b = moveBounds(layout, defs, id);
    if (!b)
        return false;
    const to = b.index + delta;
    return to >= b.min && to <= b.max;
}
/** Группа колонки — для меню (подзаголовки «Закреплены слева/справа»). */
export function columnGroup(pin) {
    return groupOf(pin);
}
//# sourceMappingURL=columns-state.js.map